import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { deleteOffer, getOffers, getOrderClients } from '../services/orders';
import TrashButton from '../components/common/TrashButton';
import Pagination from '../components/common/Pagination';
import './OrdersPage.css';

const ITEMS_PER_PAGE = 20;
const ESTADOS = [
  { key: 'all', label: 'Todas', value: null },
  { key: 'draft', label: 'Borradores', value: 'draft' },
  { key: 'sent', label: 'Enviadas', value: 'sent' },
  { key: 'accepted', label: 'Aceptadas', value: 'accepted' },
];
const NOMBRE_ESTADO = {
  draft: 'Borrador', sent: 'Enviada', accepted: 'Aceptada',
  rejected: 'Rechazada', expired: 'Caducada',
};

// Las ofertas por líneas no tienen descripción: la pieza son sus artículos.
const piezaDe = (offer) => {
  const lineas = offer.lines || [];
  if (lineas.length) {
    return lineas
      .map((l) => (l.quantity ? `${l.quantity}x ${l.part_code || '—'}` : (l.part_code || '—')))
      .join(' · ');
  }
  return offer.description || '—';
};

const formatDate = (v) => (v ? new Date(v).toLocaleDateString('es-ES') : '—');
const formatHoras = (h) => (h > 0 ? `${h.toFixed(2).replace('.', ',')} h` : '—');
const formatEuros = (v) => (v > 0 ? v.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' }) : '—');

const OffersPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, parseInt(searchParams.get('page'), 10) || 1);
  const estado = searchParams.get('status') || 'all';
  const cliente = searchParams.get('cliente') || '';

  const [clientes, setClientes] = useState([]);
  const [offers, setOffers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const actualizar = (cambios) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(cambios).forEach(([k, v]) => {
      if (v === null || v === 'all' || v === 1) next.delete(k);
      else next.set(k, String(v));
    });
    setSearchParams(next);
  };

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getOffers({
        skip: (page - 1) * ITEMS_PER_PAGE,
        limit: ITEMS_PER_PAGE,
        status_filter: ESTADOS.find((e) => e.key === estado)?.value,
        client_hash: cliente || null,
      });
      setOffers(data.offers || []);
      setTotal(data.total || 0);
    } catch (err) {
      setOffers([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, estado, cliente]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { getOrderClients().then(setClientes).catch(() => setClientes([])); }, []);

  // LAKPED-17: todos los borradores llevan papelera (lo pidió Paula). Si el borrador ya tiene pedido,
  // el servidor lo rechaza y aquí se enseña el motivo.
  const borrar = async (offer) => {
    if (!window.confirm(`¿Borrar el borrador de oferta de ${offer.client?.name || 'sin cliente'}?`)) return;
    try {
      await deleteOffer(offer.hash);
      cargar();
    } catch (err) {
      alert(err.response?.data?.detail || 'No se ha podido borrar la oferta');
    }
  };

  return (
    <div className="orders-container">
      <div className="orders-head">
        <h1 className="orders-title">Ofertas</h1>
        <button className="orders-new" onClick={() => navigate('/offers/new')}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 5v14M5 12h14" />
          </svg>
          Nueva oferta
        </button>
      </div>

      <div className="orders-toolbar">
        <div className="orders-status-filter">
          {ESTADOS.map((e) => (
            <button key={e.key} className={`orders-status-btn ${estado === e.key ? 'active' : ''}`}
                    onClick={() => actualizar({ status: e.key, page: 1 })}>
              {e.label}
            </button>
          ))}
          <select
            className="orders-cliente"
            value={cliente}
            onChange={(e) => actualizar({ cliente: e.target.value || null, page: 1 })}
          >
            <option value="">Todos los clientes</option>
            {clientes.map((c) => <option key={c.hash} value={c.hash}>{c.name}</option>)}
          </select>
        </div>
      </div>

      <div className="orders-table-wrapper">
        <table className="orders-table">
          <thead>
            <tr>
              <th>Cliente</th>
              <th>Pieza</th>
              <th>Fecha</th>
              <th className="col-num">Horas</th>
              <th className="col-num">Importe</th>
              <th className="col-estado">Estado</th>
              <th className="col-papelera" />
            </tr>
          </thead>
          <tbody>
            {loading && offers.length === 0 && (
              <tr><td colSpan="7" className="orders-empty">Cargando…</td></tr>
            )}
            {!loading && offers.length === 0 && (
              <tr><td colSpan="7" className="orders-empty">Todavía no hay ofertas</td></tr>
            )}
            {offers.map((offer) => (
              <tr key={offer.hash} className="orders-row" onClick={() => navigate(`/offers/${offer.hash}`)}>
                <td className="col-cliente">{offer.client?.name || '—'}</td>
                <td className="col-trabajo"><div className="orders-desc">{piezaDe(offer)}</div></td>
                <td>{formatDate(offer.created)}</td>
                <td className="col-num">{formatHoras(offer.estimated_hours)}</td>
                <td className="col-num">{formatEuros(offer.estimated_amount)}</td>
                <td className="col-estado">
                  <span className={`offer-badge ${offer.status}`}>{NOMBRE_ESTADO[offer.status] || offer.status}</span>
                </td>
                <td className="col-papelera">
                  {offer.status === 'draft' && (
                    <TrashButton title="Borrar el borrador" onClick={() => borrar(offer)} />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        page={page}
        totalPages={Math.max(1, Math.ceil(total / ITEMS_PER_PAGE))}
        totalItems={total}
        itemLabel={total === 1 ? 'oferta' : 'ofertas'}
        loading={loading}
        onChange={(target) => actualizar({ page: target })}
      />
    </div>
  );
};

export default OffersPage;
